import { cutoutBlocker } from "@cva/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Undo2, Wand2 } from "lucide-react";
import { useState } from "react";
import { Progress } from "../components/ui/card";
import { estimateProgress, formatRemaining, useElapsed } from "../lib/activity";
import { cn } from "../lib/utils";
import { type CutoutTarget, restoreOriginal, startCutout, usePendingCutout } from "./cutout";
import { useEnv } from "./env";
import { useEditor, useEditorApi } from "./store";

/**
 * "Hintergrund entfernen" for an image element or a still on a video track:
 * starts the background removal, shows its progress and, on a cut-out image,
 * offers to put the original back.
 */
export function CutoutControl({
  target,
  assetId,
  disabled,
  className,
}: {
  target: CutoutTarget;
  assetId: string;
  /** e.g. a locked element */
  disabled?: boolean;
  className?: string;
}) {
  const env = useEnv();
  const store = useEditorApi();
  const queryClient = useQueryClient();
  const readOnly = useEditor((s) => s.readOnly) || Boolean(disabled);
  const pending = usePendingCutout(target);
  const [restoring, setRestoring] = useState(false);
  const asset = env.assets.get(assetId);
  if (asset?.kind !== "image") return null;

  if (pending) return <CutoutProgress pending={pending} className={className} />;

  if (asset.cutout) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <span
          className="inline-flex h-8 min-w-0 flex-1 items-center gap-1.5 truncate rounded-md bg-emerald-50 px-2.5 text-xs font-medium text-emerald-800"
          title="Der Hintergrund ist entfernt (freigestellte Kopie aus der Mediathek)"
        >
          <Wand2 className="size-3.5 shrink-0" /> Freigestellt
        </span>
        <button
          type="button"
          disabled={readOnly || restoring}
          onClick={async () => {
            setRestoring(true);
            try {
              await restoreOriginal({ orgId: env.orgId, store, queryClient, target, copy: asset });
            } finally {
              setRestoring(false);
            }
          }}
          className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-zinc-600 hover:bg-zinc-100 disabled:opacity-50"
          title="Das Bild mit Hintergrund wieder einsetzen"
        >
          {restoring ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Undo2 className="size-3.5" />
          )}
          Original
        </button>
      </div>
    );
  }

  const blocker = cutoutBlocker({
    kind: "image",
    status: asset.status ?? "ready",
    width: asset.width,
    height: asset.height,
    cutout: null,
  });
  return (
    <button
      type="button"
      disabled={readOnly || Boolean(blocker)}
      title={blocker ?? "Das Motiv freistellen: Der Hintergrund wird durchsichtig."}
      onClick={() =>
        void startCutout({ orgId: env.orgId, store, queryClient, target, source: asset })
      }
      className={cn(
        "inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-sm font-medium text-zinc-700 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-800 disabled:opacity-50 disabled:hover:border-zinc-200 disabled:hover:bg-transparent disabled:hover:text-zinc-700",
        className,
      )}
    >
      <Wand2 className="size-4" /> Hintergrund entfernen
    </button>
  );
}

function CutoutProgress({
  pending,
  className,
}: {
  pending: NonNullable<ReturnType<typeof usePendingCutout>>;
  className?: string;
}) {
  const info = pending.copy?.cutout ?? null;
  const elapsed = useElapsed(info?.elapsedMs ?? null);
  const estimate = estimateProgress(info?.progress ?? 0, elapsed, info?.estimatedMs ?? null);
  const started = info?.elapsedMs !== null && info?.elapsedMs !== undefined;
  return (
    <div className={cn("rounded-md border border-brand-100 bg-brand-50/60 px-2.5 py-2", className)}>
      <p className="flex items-center gap-1.5 text-xs font-medium text-brand-900">
        <Loader2 className="size-3.5 animate-spin" />
        {started ? "Hintergrund wird entfernt …" : "Freistellen startet …"}
        {started && estimate.remainingMs !== null ? (
          <span className="ml-auto font-normal text-brand-700">
            {formatRemaining(Math.max(0, estimate.remainingMs))}
          </span>
        ) : null}
      </p>
      <Progress className="mt-1.5 h-1.5" value={started ? estimate.progress : 2} />
      <p className="mt-1 text-[11px] text-zinc-500">
        Du kannst weiterarbeiten – das Bild wird danach automatisch ersetzt.
      </p>
    </div>
  );
}
